import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_LABEL = {
  super_admin: { label: 'Super admin', color: 'error' },
  admin: { label: 'Administrateur', color: 'primary' },
  relecteur: { label: 'Relecteur', color: 'success' },
};

const EMPTY_FORM = { email: '', first_name: '', last_name: '', role: 'relecteur' };

function Users() {
  const { user: currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // { email, password } affiche une seule fois apres creation / reinitialisation
  const [credentials, setCredentials] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userService.getAll();
      setUsers(res.data.users);
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err);
      enqueueSnackbar('Erreur lors du chargement des utilisateurs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (currentUser && currentUser.role === 'super_admin') load();
  }, [load, currentUser]);

  if (!currentUser || currentUser.role !== 'super_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Cette page est réservée au super administrateur.</Alert>
      </Box>
    );
  }

  const apiMessage = (err, fallback) =>
    (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || fallback;

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await userService.create(form);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setCredentials({ email: res.data.user.email, password: res.data.temporary_password });
      load();
    } catch (err) {
      enqueueSnackbar(apiMessage(err, 'Erreur lors de la création du compte'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await userService.update(id, data);
      setUsers(prev => prev.map(u => (u.id === id ? res.data.user : u)));
    } catch (err) {
      enqueueSnackbar(apiMessage(err, 'Erreur lors de la modification'), { variant: 'error' });
    }
  };

  const handleResetPassword = async (target) => {
    if (!window.confirm(`Générer un nouveau mot de passe temporaire pour ${target.email} ? L'ancien ne fonctionnera plus.`)) return;
    try {
      const res = await userService.resetPassword(target.id);
      setCredentials({ email: target.email, password: res.data.temporary_password });
    } catch (err) {
      enqueueSnackbar(apiMessage(err, 'Erreur lors de la réinitialisation'), { variant: 'error' });
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(credentials.password);
      enqueueSnackbar('Mot de passe copié', { variant: 'success' });
    } catch (_) {
      enqueueSnackbar('Copie impossible, sélectionnez le mot de passe à la main', { variant: 'warning' });
    }
  };

  const canManage = (u) => u.role !== 'super_admin' && u.id !== currentUser.id;
  const formValid = /\S+@\S+\.\S+/.test(form.email);

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">👥 Utilisateurs et relecteurs</Typography>
            <Box>
              <Tooltip title="Actualiser">
                <IconButton onClick={load} size="small" disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateOpen(true)} sx={{ ml: 1 }}>
                Nouveau compte
              </Button>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Un <strong>relecteur</strong> peut vérifier les entrées du dictionnaire et proposer des corrections, sans rien
            modifier lui-même. Un <strong>administrateur</strong> gère en plus l&apos;application. Un abonné WhatsApp n&apos;a pas
            de compte : il faut créer ici un compte (e-mail + mot de passe) pour lui donner l&apos;accès relecteur.
          </Alert>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rôle</TableCell>
                    <TableCell align="center">Actif</TableCell>
                    <TableCell>Dernière connexion</TableCell>
                    <TableCell align="center">Mot de passe</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} sx={{ opacity: u.is_active ? 1 : 0.55 }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {`${u.first_name || ''} ${u.last_name || ''}`.trim() || '-'}
                        {u.id === currentUser.id && <Chip label="vous" size="small" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {canManage(u) ? (
                          <Select
                            size="small"
                            value={u.role}
                            onChange={(e) => handleUpdate(u.id, { role: e.target.value })}
                            sx={{ minWidth: 150 }}
                          >
                            <MenuItem value="relecteur">Relecteur</MenuItem>
                            <MenuItem value="admin">Administrateur</MenuItem>
                          </Select>
                        ) : (
                          <Chip
                            label={ROLE_LABEL[u.role]?.label || u.role}
                            size="small"
                            color={ROLE_LABEL[u.role]?.color || 'default'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={!!u.is_active}
                          disabled={!canManage(u)}
                          onChange={(e) => handleUpdate(u.id, { is_active: e.target.checked })}
                        />
                      </TableCell>
                      <TableCell>{u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : 'jamais'}</TableCell>
                      <TableCell align="center">
                        {canManage(u) && (
                          <Tooltip title="Générer un nouveau mot de passe temporaire">
                            <IconButton size="small" onClick={() => handleResetPassword(u)}>
                              <LockResetIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nouveau compte</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            autoFocus
            sx={{ mt: 1 }}
          />
          <TextField label="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <TextField label="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <FormControl>
            <InputLabel id="role-label">Rôle</InputLabel>
            <Select labelId="role-label" label="Rôle" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <MenuItem value="relecteur">Relecteur</MenuItem>
              <MenuItem value="admin">Administrateur</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Un mot de passe temporaire sera généré et affiché une seule fois à l&apos;étape suivante.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>Annuler</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formValid || saving}>
            {saving ? 'Création...' : 'Créer le compte'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!credentials} onClose={() => setCredentials(null)} fullWidth maxWidth="xs">
        <DialogTitle>Mot de passe temporaire</DialogTitle>
        <DialogContent>
          {credentials && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Notez-le maintenant : il ne sera <strong>plus affiché</strong>. Transmettez-le à la personne, qui pourra le
                changer après sa première connexion.
              </Alert>
              <Typography variant="body2">Compte : <strong>{credentials.email}</strong></Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField value={credentials.password} fullWidth InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }} />
                <Tooltip title="Copier">
                  <IconButton onClick={copyPassword}><CopyIcon /></IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCredentials(null)}>J&apos;ai noté le mot de passe</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Users;
